/**
 * Screen header — single stack API.
 *
 * Modes via props (not variants):
 * - default bar: centered title, outline back, optional scroll title shrink
 * - `titleMode="inline"`: row title + elevated back (detail / WebView overlays)
 * - `collapseOnScroll`: large title + shell height collapse (Calendar / Accounts hubs)
 *
 * Omit `onBack` for title-only / tab hubs. Bespoke menu/dashboard chrome stays separate.
 */
import React, { useMemo } from 'react';
import { View, Text, StyleSheet, type StyleProp, type ViewStyle } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  useAnimatedScrollHandler,
  interpolate,
  Extrapolation,
  type SharedValue,
} from 'react-native-reanimated';
import { useTranslation } from '../../shared/i18n';
import { appTheme, useThemeColors } from '../../shared/theme';
import { useRtl } from '../../shared/i18n/useRtl';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { androidHeaderTopInset, androidScaleFont } from '../../shared/theme/sharedStyles';
import { androidScaleSpace, isAndroidUi } from '../../shared/theme/androidUiParity';
import {
  DEFAULT_HEADER_SCROLL_THRESHOLD,
  HEADER_HORIZONTAL_PADDING,
  HEADER_BACK_ICON_SIZE_INLINE,
  SCREEN_HEADER_HEIGHT,
  SCREEN_HEADER_HEIGHT_COMPACT,
  TAB_HEADER_EXPANDED_HEIGHT,
  TAB_HEADER_COLLAPSED_HEIGHT,
  TAB_HEADER_SCROLL_THRESHOLD,
  CHROME_REVEAL_OPACITY_MAX,
  resolveScreenHeaderContentHeight,
  resolveHeaderSideSlotWidth,
} from '../../shared/theme/styles/headerTokens';
import { ScrollChromeUnderlay, type ScrollChromeMode } from './ScrollChromeUnderlay';
import {
  HeaderBackButton,
  useHeaderBackScrollFillStyle,
  HEADER_BACK_ICON_SIZE_STACK,
} from './HeaderBackButton';
import type { HeaderBackButtonAppearance } from './HeaderBackButton';

export {
  DEFAULT_HEADER_SCROLL_THRESHOLD,
  SCREEN_HEADER_HEIGHT,
  SCREEN_HEADER_HEIGHT_COMPACT,
  HEADER_SIDE_SLOT_WIDTH,
  TAB_HEADER_EXPANDED_HEIGHT,
  TAB_HEADER_EXPANDED_HEIGHT_COMPACT,
  TAB_HEADER_COLLAPSED_HEIGHT,
  TAB_HEADER_SCROLL_THRESHOLD,
  resolveHeaderSideSlotWidth,
  resolveScreenHeaderContentHeight,
} from '../../shared/theme/styles/headerTokens';

export type { ScrollChromeMode };

/**
 * Scroll content `paddingTop` under an absolute header when the header chrome
 * **fills** the safe-area band (opaque / solid). Always pair with
 * `contentInsetAdjustmentBehavior: 'never'` so iOS does not add a second inset.
 *
 * For transparent reveal tab headers (Orders) and collapsing hubs (Calendar / Accounts),
 * prefer `useHubHeaderTopPadding` instead — on iOS that skips a second safe-area void.
 */
export function useHeaderTopPadding(compact_or_height: boolean | number = false, extra_below = 0): number {
  const insets = useSafeAreaInsets();
  const content_height =
    typeof compact_or_height === 'number'
      ? compact_or_height
      : resolveScreenHeaderContentHeight(compact_or_height);
  const extra = extra_below > 0 ? androidScaleSpace(extra_below) : 0;
  return androidHeaderTopInset(insets.top) + content_height + extra;
}

/**
 * Scroll padding under transparent / collapsing hub headers (Calendar, Accounts, Orders tab).
 * Android includes status-bar inset; iOS uses the content band only so the first
 * card sits under the transparent shell toward the title (tight header→card gap).
 * Dashboard uses its own full-clearance math and does not call this helper.
 *
 * Always pair with `SCROLL_VIEW_PROPS` / `contentInsetAdjustmentBehavior: 'never'`.
 */
export function useHubHeaderTopPadding(expanded_height: number, extra_below = 0): number {
  const insets = useSafeAreaInsets();
  const top = isAndroidUi() ? androidHeaderTopInset(insets.top) : 0;
  const extra = extra_below > 0 ? androidScaleSpace(extra_below) : 0;
  return top + expanded_height + extra;
}

/** Wire `scrollY` + `onScroll` for scroll-linked header chrome. */
export function useScreenHeaderScroll() {
  const scroll_y = useSharedValue(0);
  const onScroll = useAnimatedScrollHandler({
    onScroll: event => {
      scroll_y.value = event.contentOffset.y;
    },
  });
  return { scrollY: scroll_y, onScroll };
}

export {
  HeaderBackButton,
  HeaderIconButton,
  useHeaderBackScrollFillStyle,
  HEADER_BACK_BUTTON_SIZE,
  HEADER_BACK_ICON_SIZE_STACK,
  HEADER_BACK_ICON_SIZE_INLINE,
} from './HeaderBackButton';
export type { HeaderBackButtonAppearance } from './HeaderBackButton';

export type ScreenHeaderTitleMode = 'overlay' | 'inline';

type ScreenHeaderProps = {
  title: string;
  titleCompact?: string;
  subtitle?: string;
  /** Omit for title-only / tab hubs (Calendar, Accounts). */
  onBack?: () => void;
  backAppearance?: HeaderBackButtonAppearance;
  leftAction?: React.ReactNode;
  rightAction?: React.ReactNode;
  bottomContent?: React.ReactNode;
  compact?: boolean;
  children?: React.ReactNode;
  scrollY?: SharedValue<number>;
  scrollThreshold?: number;
  /**
   * Large title + animated shell height on scroll (Calendar / Accounts hubs).
   * Requires `scrollY`. Prefer passing matching height into `useHeaderTopPadding(expandedHeight)`.
   */
  collapseOnScroll?: boolean;
  expandedHeight?: number;
  collapsedHeight?: number;
  bottomContentMaxHeight?: number;
  bottomContentMarginTop?: number;
  /**
   * `overlay` = centered title between side slots (default push screens).
   * `inline` = flex title row with elevated back (detail / WebView).
   */
  titleMode?: ScreenHeaderTitleMode;
  /** Override default side-slot width (e.g. wide trailing actions like “Past orders”). */
  sideSlotWidth?: number;
  textAlign?: 'left' | 'right' | 'center';
  androidCompactTypography?: boolean;
  backgroundColor?: string;
  scrollChrome?: ScrollChromeMode;
  scrollChromeOpacity?: number;
  scrollChromeColor?: string;
  showScrollChromeBorder?: boolean;
  style?: StyleProp<ViewStyle>;
};

type ChromeBits = {
  shell_bg: string;
  use_reveal_chrome: boolean;
  scroll_y: SharedValue<number> | undefined;
  chrome_fill_color: string;
  chrome_border_color: string | undefined;
  chrome_opacity_max: number;
};

function resolveScrollChromeMode(
  scroll_chrome: ScrollChromeMode | undefined,
  scroll_y: SharedValue<number> | undefined,
  static_background?: string,
): ScrollChromeMode {
  if (scroll_chrome) {
    return scroll_chrome;
  }
  if (static_background) {
    return 'always';
  }
  if (scroll_y) {
    return 'reveal';
  }
  return 'off';
}

function ScrollChrome({
  chrome,
  threshold,
}: {
  chrome: ChromeBits;
  threshold: number;
}) {
  if (!chrome.use_reveal_chrome || chrome.scroll_y == null) {
    return null;
  }
  return (
    <ScrollChromeUnderlay
      scrollY={chrome.scroll_y}
      backgroundColor={chrome.chrome_fill_color}
      borderColor={chrome.chrome_border_color}
      threshold={threshold}
      maxOpacity={chrome.chrome_opacity_max}
    />
  );
}

function wrapWithChildren(bar: React.ReactNode, children?: React.ReactNode) {
  if (children == null) {
    return bar;
  }
  return (
    <View style={styles.screenWrap}>
      {bar}
      <View style={styles.contentWrap}>{children}</View>
    </View>
  );
}

function InlineHeaderBar({
  title,
  onBack,
  leftAction,
  rightAction,
  backAppearance,
  textAlign,
  writingDirection,
  safeTopInset,
  scrollThreshold,
  chrome,
  style,
}: {
  title: string;
  onBack?: () => void;
  leftAction?: React.ReactNode;
  rightAction?: React.ReactNode;
  backAppearance: HeaderBackButtonAppearance;
  textAlign: 'left' | 'right' | 'center';
  writingDirection: 'rtl' | 'ltr';
  safeTopInset: number;
  scrollThreshold: number;
  chrome: ChromeBits;
  style?: StyleProp<ViewStyle>;
}) {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();

  const back_or_left = onBack ? (
    <HeaderBackButton
      onPress={onBack}
      accessibilityLabel={t('common.back')}
      iconSize={HEADER_BACK_ICON_SIZE_INLINE}
      appearance={backAppearance}
    />
  ) : (
    leftAction
  );
  const trailing = <View style={styles.inlineRight}>{rightAction}</View>;
  const title_or_spacer = title ? (
    <Text
      style={[styles.inlineTitle, { color: colors.text, textAlign, writingDirection }]}
      numberOfLines={1}>
      {title}
    </Text>
  ) : (
    <View style={styles.inlineTitleSpacer} />
  );

  return (
    <View style={[styles.inlineWrap, { paddingTop: safeTopInset, backgroundColor: chrome.shell_bg }, style]}>
      <ScrollChrome chrome={chrome} threshold={scrollThreshold} />
      <View style={[styles.inlineRow, horizontalLtrDirectionStyle]}>
        {rtl.isRTL ? trailing : back_or_left}
        {title_or_spacer}
        {rtl.isRTL ? back_or_left : trailing}
      </View>
    </View>
  );
}

function HeroHeaderBar({
  title,
  subtitle,
  rightAction,
  bottomContent,
  scrollY,
  expandedHeight,
  collapsedHeight,
  bottomContentMaxHeight,
  bottomContentMarginTop,
  textAlign,
  writingDirection,
  androidCompactTypography,
  safeTopInset,
  chrome,
  style,
}: {
  title: string;
  subtitle?: string;
  rightAction?: React.ReactNode;
  bottomContent?: React.ReactNode;
  scrollY: SharedValue<number>;
  expandedHeight: number;
  collapsedHeight: number;
  bottomContentMaxHeight: number;
  bottomContentMarginTop: number;
  textAlign: 'left' | 'right' | 'center';
  writingDirection: 'rtl' | 'ltr';
  androidCompactTypography: boolean;
  safeTopInset: number;
  chrome: ChromeBits;
  style?: StyleProp<ViewStyle>;
}) {
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const hero_threshold = TAB_HEADER_SCROLL_THRESHOLD;
  const title_font_expanded = androidScaleFont(androidCompactTypography ? 22 : 26);
  const title_font_collapsed = androidScaleFont(androidCompactTypography ? 16 : 18);
  const title_scale_collapsed = title_font_collapsed / title_font_expanded;
  const show_sub = Boolean(subtitle);
  const show_bottom = Boolean(bottomContent);
  const title_only = !show_sub && !show_bottom;

  /**
   * Safe area lives ONLY on the outer shell as `paddingTop` — content height animates
   * separately. Never add `safeTopInset` into the animated height (that + paddingTop
   * doubles the top gap on iOS content-box layout).
   */
  const content_height_style = useAnimatedStyle(() => ({
    height: interpolate(
      scrollY.value,
      [0, hero_threshold * 0.75, hero_threshold],
      [expandedHeight, collapsedHeight, collapsedHeight],
      Extrapolation.CLAMP,
    ),
  }));

  const title_size_style = useAnimatedStyle(() => {
    const scale = interpolate(
      scrollY.value,
      [0, hero_threshold * 0.7],
      [1, title_scale_collapsed],
      Extrapolation.CLAMP,
    );
    return { transform: [{ scale }] };
  });

  const subtitle_style = useAnimatedStyle(() => {
    if (!show_sub) {
      return { opacity: 0, transform: [{ translateY: 0 }] };
    }
    const visible = interpolate(scrollY.value, [0, hero_threshold * 0.58], [1, 0], Extrapolation.CLAMP);
    return {
      opacity: visible,
      transform: [{ translateY: (1 - visible) * -4 }],
    };
  });

  const bottom_style = useAnimatedStyle(() => {
    if (!show_bottom) {
      return { opacity: 0, transform: [{ translateY: 0 }] };
    }
    const visible = interpolate(scrollY.value, [0, hero_threshold * 0.58], [1, 0], Extrapolation.CLAMP);
    return {
      opacity: visible,
      transform: [{ translateY: (1 - visible) * -4 }],
    };
  });

  return (
    <View style={[styles.heroWrap, { paddingTop: safeTopInset, backgroundColor: chrome.shell_bg }, style]}>
      <ScrollChrome chrome={chrome} threshold={hero_threshold} />
      <Animated.View
        style={[
          styles.heroInner,
          title_only ? styles.heroInnerTitleOnly : null,
          { paddingHorizontal: HEADER_HORIZONTAL_PADDING },
          content_height_style,
        ]}>
        <View style={[styles.heroTitleRow, horizontalLtrDirectionStyle]}>
          {rtl.isRTL && rightAction ? <View style={styles.heroRightSlot}>{rightAction}</View> : null}
          <View style={styles.heroTitleBlock}>
            <Animated.Text
              numberOfLines={1}
              style={[
                styles.heroTitle,
                { color: colors.text, textAlign, writingDirection },
                title_size_style,
              ]}>
              {title}
            </Animated.Text>
            {show_sub ? (
              <Animated.Text
                numberOfLines={1}
                style={[
                  styles.heroSubtitle,
                  { color: colors.textLight, textAlign, writingDirection },
                  subtitle_style,
                ]}>
                {subtitle}
              </Animated.Text>
            ) : null}
          </View>
          {!rtl.isRTL && rightAction ? <View style={styles.heroRightSlot}>{rightAction}</View> : null}
        </View>
        {show_bottom ? <Animated.View style={bottom_style}>{bottomContent}</Animated.View> : null}
      </Animated.View>
    </View>
  );
}

function StackHeaderBar({
  title,
  titleCompact,
  onBack,
  leftAction,
  rightAction,
  bottomContent,
  compact,
  scrollY,
  scrollThreshold,
  backAppearance,
  textAlign,
  writingDirection,
  safeTopInset,
  chrome,
  style,
  sideSlotWidth,
}: {
  title: string;
  titleCompact?: string;
  onBack?: () => void;
  leftAction?: React.ReactNode;
  rightAction?: React.ReactNode;
  bottomContent?: React.ReactNode;
  compact: boolean;
  scrollY?: SharedValue<number>;
  scrollThreshold: number;
  backAppearance: HeaderBackButtonAppearance;
  textAlign: 'left' | 'right' | 'center';
  writingDirection: 'rtl' | 'ltr';
  safeTopInset: number;
  chrome: ChromeBits;
  style?: StyleProp<ViewStyle>;
  sideSlotWidth?: number;
}) {
  const { t } = useTranslation();
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const side_slot_width = sideSlotWidth ?? resolveHeaderSideSlotWidth();
  const content_height = compact ? SCREEN_HEADER_HEIGHT_COMPACT : SCREEN_HEADER_HEIGHT;
  const header_height_expanded = resolveScreenHeaderContentHeight(compact);
  const stack_title_font_expanded = androidScaleFont(17);
  const stack_title_font_collapsed = androidScaleFont(15);
  const use_legacy_title_swap = scrollY != null && titleCompact != null && titleCompact !== '';
  const use_stack_scroll = scrollY != null && !use_legacy_title_swap;
  const stack_back_fill_style = useHeaderBackScrollFillStyle(
    scrollY,
    scrollThreshold,
    colors.surface,
    colors.borderSubtle,
  );

  const side_slot_style = useMemo(() => ({ width: side_slot_width }), [side_slot_width]);
  const title_overlay_style = useMemo(
    () => [styles.titleOverlay, { paddingHorizontal: side_slot_width }],
    [side_slot_width],
  );
  const stack_title_scale_collapsed = stack_title_font_collapsed / stack_title_font_expanded;

  const renderBackAction = (with_scroll_fill: boolean) =>
    onBack ? (
      <HeaderBackButton
        onPress={onBack}
        accessibilityLabel={t('common.back')}
        iconSize={HEADER_BACK_ICON_SIZE_STACK}
        appearance={backAppearance}
        shellStyle={with_scroll_fill ? stack_back_fill_style : undefined}
      />
    ) : (
      leftAction
    );

  /** Fixed height — avoid Yoga thrash every scroll frame (absolute header). */
  const contentRowAnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    return { height: header_height_expanded };
  }, [header_height_expanded]);

  /** Compositor-friendly shrink (no fontSize layout). */
  const stackTitleSizeStyle = useAnimatedStyle(() => {
    'worklet';
    if (scrollY == null) {
      return {};
    }
    const scale = interpolate(
      scrollY.value,
      [0, scrollThreshold],
      [1, stack_title_scale_collapsed],
      Extrapolation.CLAMP,
    );
    return { transform: [{ scale }] };
  }, [scrollY, scrollThreshold, stack_title_scale_collapsed]);

  const title1AnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    if (scrollY == null) {
      return { opacity: 1 };
    }
    return { opacity: interpolate(scrollY.value, [0, scrollThreshold], [1, 0], Extrapolation.CLAMP) };
  }, [scrollY, scrollThreshold]);

  const title2AnimatedStyle = useAnimatedStyle(() => {
    'worklet';
    if (scrollY == null) {
      return { opacity: 0 };
    }
    return { opacity: interpolate(scrollY.value, [0, scrollThreshold], [0, 1], Extrapolation.CLAMP) };
  }, [scrollY, scrollThreshold]);

  const title_text_style = [
    styles.title,
    { color: colors.text, textAlign, writingDirection },
  ];

  const renderContentRow = (with_scroll_fill: boolean, height_style: StyleProp<ViewStyle>) => {
    const leading = renderBackAction(with_scroll_fill);
    const trailing = rightAction ?? null;
    const leading_slot_align = rtl.isRTL ? styles.sideSlotEnd : styles.sideSlotStart;
    const trailing_slot_align = rtl.isRTL ? styles.sideSlotStart : styles.sideSlotEnd;
    return (
      <Animated.View style={[styles.headerContent, horizontalLtrDirectionStyle, height_style]}>
        <View style={[styles.sideSlot, side_slot_style, rtl.isRTL ? trailing_slot_align : leading_slot_align]}>
          {rtl.isRTL ? trailing : leading}
        </View>
        <View style={title_overlay_style} pointerEvents="none">
          {use_legacy_title_swap ? (
            <View style={styles.titleSwapWrap}>
              <Animated.View style={[styles.titleInner, title1AnimatedStyle]}>
                <Text style={title_text_style} numberOfLines={1}>
                  {title}
                </Text>
              </Animated.View>
              <Animated.View style={[styles.titleInner, title2AnimatedStyle]}>
                <Text style={[title_text_style, styles.titleCompact]} numberOfLines={1}>
                  {titleCompact}
                </Text>
              </Animated.View>
            </View>
          ) : use_stack_scroll ? (
            <Animated.Text numberOfLines={1} style={[title_text_style, stackTitleSizeStyle]}>
              {title}
            </Animated.Text>
          ) : (
            <Text style={[title_text_style, compact && styles.titleCompact]} numberOfLines={1}>
              {title}
            </Text>
          )}
        </View>
        <View style={[styles.sideSlot, side_slot_style, rtl.isRTL ? leading_slot_align : trailing_slot_align]}>
          {rtl.isRTL ? leading : trailing}
        </View>
      </Animated.View>
    );
  };

  return (
    <View style={[styles.wrapper, { paddingTop: safeTopInset, backgroundColor: chrome.shell_bg }, style]}>
      <ScrollChrome chrome={chrome} threshold={scrollThreshold} />
      {use_legacy_title_swap || use_stack_scroll
        ? renderContentRow(true, contentRowAnimatedStyle)
        : renderContentRow(false, { height: content_height })}
      {bottomContent ? (
        <View style={[styles.bottomContent, compact && styles.bottomContentCompact]}>{bottomContent}</View>
      ) : null}
    </View>
  );
}

export const ScreenHeader = React.memo<ScreenHeaderProps>(function ScreenHeader(props) {
  const {
    title,
    titleCompact,
    subtitle,
    onBack,
    backAppearance,
    leftAction,
    rightAction,
    bottomContent,
    compact = false,
    children,
    scrollY,
    scrollThreshold = DEFAULT_HEADER_SCROLL_THRESHOLD,
    collapseOnScroll = false,
    expandedHeight = TAB_HEADER_EXPANDED_HEIGHT,
    collapsedHeight = TAB_HEADER_COLLAPSED_HEIGHT,
    bottomContentMaxHeight = 0,
    bottomContentMarginTop = appTheme.spacing.sm,
    titleMode = 'overlay',
    sideSlotWidth,
    textAlign,
    androidCompactTypography = false,
    backgroundColor,
    scrollChrome,
    scrollChromeOpacity,
    scrollChromeColor,
    showScrollChromeBorder = true,
    style,
  } = props;

  const insets = useSafeAreaInsets();
  const colors = useThemeColors() ?? appTheme.colors;
  const rtl = useRtl();
  const safe_top_inset = androidHeaderTopInset(insets.top);
  const is_inline = titleMode === 'inline';
  const is_hero = collapseOnScroll && scrollY != null;
  const resolved_back_appearance: HeaderBackButtonAppearance =
    backAppearance ?? (is_inline ? 'elevated' : 'outline');
  const resolved_text_align =
    textAlign ?? (is_hero || is_inline ? (rtl.isRTL ? 'right' : 'left') : 'center');
  const writing_direction = rtl.isRTL ? 'rtl' : 'ltr';

  const chrome_bg = backgroundColor ?? 'transparent';
  const scroll_chrome_mode = resolveScrollChromeMode(scrollChrome, scrollY, backgroundColor);
  const chrome: ChromeBits = {
    shell_bg: scroll_chrome_mode === 'always' ? chrome_bg : 'transparent',
    use_reveal_chrome: scroll_chrome_mode === 'reveal' && scrollY != null,
    scroll_y: scrollY,
    chrome_fill_color: scrollChromeColor ?? colors.surface,
    chrome_border_color: showScrollChromeBorder ? colors.borderSubtle : undefined,
    chrome_opacity_max: scrollChromeOpacity ?? CHROME_REVEAL_OPACITY_MAX,
  };

  if (is_inline && !is_hero) {
    return wrapWithChildren(
      <InlineHeaderBar
        title={title}
        onBack={onBack}
        leftAction={leftAction}
        rightAction={rightAction}
        backAppearance={resolved_back_appearance}
        textAlign={resolved_text_align}
        writingDirection={writing_direction}
        safeTopInset={safe_top_inset}
        scrollThreshold={scrollThreshold}
        chrome={chrome}
        style={style}
      />,
      children,
    );
  }

  if (is_hero) {
    return wrapWithChildren(
      <HeroHeaderBar
        title={title}
        subtitle={subtitle}
        rightAction={rightAction}
        bottomContent={bottomContent}
        scrollY={scrollY!}
        expandedHeight={expandedHeight}
        collapsedHeight={collapsedHeight}
        bottomContentMaxHeight={bottomContentMaxHeight}
        bottomContentMarginTop={bottomContentMarginTop}
        textAlign={resolved_text_align}
        writingDirection={writing_direction}
        androidCompactTypography={androidCompactTypography}
        safeTopInset={safe_top_inset}
        chrome={chrome}
        style={style}
      />,
      children,
    );
  }

  return wrapWithChildren(
    <StackHeaderBar
      title={title}
      titleCompact={titleCompact}
      onBack={onBack}
      leftAction={leftAction}
      rightAction={rightAction}
      bottomContent={bottomContent}
      compact={compact}
      scrollY={scrollY}
      scrollThreshold={scrollThreshold}
      backAppearance={resolved_back_appearance}
      textAlign={resolved_text_align}
      writingDirection={writing_direction}
      safeTopInset={safe_top_inset}
      chrome={chrome}
      style={style}
      sideSlotWidth={sideSlotWidth}
    />,
    children,
  );
});

const styles = StyleSheet.create({
  screenWrap: { flex: 1 },
  contentWrap: { flex: 1 },
  wrapper: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 1000,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: appTheme.spacing.sm,
    position: 'relative',
  },
  sideSlot: {
    zIndex: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  sideSlotStart: {
    alignItems: 'flex-start',
  },
  sideSlotEnd: {
    alignItems: 'flex-end',
  },
  titleOverlay: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  titleSwapWrap: {
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: androidScaleFont(17),
  },
  titleInner: {
    position: 'absolute',
    left: 0,
    right: 0,
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: { fontSize: androidScaleFont(17), fontWeight: '700', letterSpacing: -0.4 },
  titleCompact: { fontSize: androidScaleFont(15) },
  bottomContent: { paddingBottom: appTheme.spacing.sm },
  bottomContentCompact: { paddingBottom: appTheme.spacing.xs },
  inlineWrap: { position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000 },
  inlineRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: HEADER_HORIZONTAL_PADDING,
    minHeight: SCREEN_HEADER_HEIGHT,
    gap: appTheme.spacing.sm,
  },
  inlineTitle: { flex: 1, ...appTheme.typography.body, fontWeight: '700' },
  inlineTitleSpacer: { flex: 1, minWidth: 0 },
  inlineRight: { minWidth: 40, alignItems: 'center', justifyContent: 'center' },
  heroWrap: { position: 'absolute', left: 0, right: 0, top: 0, zIndex: 100, overflow: 'hidden' },
  heroInner: {
    paddingTop: appTheme.spacing.sm,
    paddingBottom: 0,
    justifyContent: 'flex-end',
    overflow: 'hidden',
  },
  /** Title-only hubs (Calendar) — keep title near the top in expanded + collapsed. */
  heroInnerTitleOnly: {
    justifyContent: 'flex-start',
    paddingTop: appTheme.spacing.xs,
    paddingBottom: 0,
  },
  heroTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: appTheme.spacing.sm,
  },
  heroTitleBlock: { flex: 1, minWidth: 0 },
  heroTitle: { fontSize: androidScaleFont(26), fontWeight: '800', letterSpacing: -0.5 },
  heroSubtitle: { fontSize: androidScaleFont(13), lineHeight: androidScaleFont(18), fontWeight: '500', marginTop: 6 },
  heroRightSlot: { flexShrink: 0, alignItems: 'center', justifyContent: 'center' },
});

export default ScreenHeader;
